import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useResponsive } from '../../components/vm/useResponsive';
import { useNotification } from '../../Notifications';
import { useAuth } from '../../components/auth/AuthContext';
import * as Sentry from "@sentry/react";

// ============================================================================
// CUSTOM SVG ICONS
// ============================================================================
const FaTimes = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const FaRedo = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
  </svg>
);

const FaExclamationTriangle = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
  </svg>
);

const FaCheck = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const FaInfoCircle = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);



const FaServer = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M20 13H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zM7 19c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM20 3H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1zM7 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
  </svg>
);

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'restore-snapshot-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'restore-snapshot-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'restore-snapshot-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// ERROR MESSAGES MAPPING
// ============================================================================
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'SNAPSHOT_LIST_FAILED': 'Unable to load snapshots. Please refresh the page.',
  'SNAPSHOT_DETAILS_FAILED': 'Unable to load snapshot details. Please try again.',
  'SNAPSHOT_CREATE_FAILED': 'Unable to create snapshot. Please try again.',
  'SNAPSHOT_DELETE_FAILED': 'Unable to delete snapshot. Please try again.',
  'SNAPSHOT_RESTORE_FAILED': 'Unable to restore snapshot. Please try again.',
  'VM_NOT_ACTIVE': 'VM must be active for snapshot operations',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'SNAPSHOT_ALREADY_EXISTS': 'A snapshot with this name already exists',
  'USER_NOT_FOUND': 'Please refresh and try again',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable',
  'INVALID_ID_FORMAT': 'Invalid request. Please try again',
  'INVALID_SNAPSHOT_PARAMETERS': 'Please check your input and try again',
  'INVALID_DATA': 'Invalid input data',
  'INVALID_REQUEST': 'Invalid request format',
  'DB_ERROR': 'Service temporarily unavailable',
  'INCOMPLETE_PROFILE': 'Please complete your profile before creating snapshots',
  'INSUFFICIENT_CREDITS': 'Insufficient account balance. Please add funds to continue',
  'VM_UNAVAILABLE': 'Selected VM is unavailable or you do not have permission to access it',
  'SNAPSHOT_NOT_FOUND': 'Snapshot not found or you do not have permission to access it',
  'INVALID_CONTRACT_ID': 'Invalid contract selection. Please try again',
  'NETWORK_ERROR': 'Network connection failed. Please check your internet connection and try again.',
  'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.'
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  
  return 'Something went wrong. Please try again.';
};

const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};

const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const validateSnapshotName = (name) => {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'Name is required' };
  }
  
  if (name.length < 2) {
    return { valid: false, error: 'Name must be at least 2 characters' };
  }
  
  if (name.length > 30) {
    return { valid: false, error: 'Name must be 30 characters or less' };
  }
  
  if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) {
    return { 
      valid: false, 
      error: 'Use only letters, numbers, spaces, hyphens, and underscores.' 
    };
  }
  
  if (/^[-_]|[-_]$/.test(name)) {
    return {
      valid: false,
      error: 'Name cannot start or end with hyphen or underscore'
    };
  }
  
  return { valid: true };
};

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isSubmitting) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '680px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '24px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '260px',
    padding: '24px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  iconWrapper: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '12px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '6px',
    display: 'block',
  },
  required: {
    color: '#ff6b6b',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '11px',
    marginTop: '4px',
  },
  infoBox: {
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  infoText: {
    color: '#93c5fd',
    fontSize: '12px',
    margin: 0,
    lineHeight: '1.5',
  },
  sourceCard: {
    backgroundColor: '#1a1a1a',
    borderRadius: '10px',
    padding: '14px',
    marginBottom: '16px',
    border: '1px solid #333',
  },
  submitBtn: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: isSubmitting ? '#3d4435' : '#e0eacc',
    color: isSubmitting ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
  },
  cancelBtn: {
    width: '100%',
    marginTop: '10px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: isSubmitting ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '12px',
    margin: '0 0 4px 0',
  },
  summaryValue: {
    color: 'white',
    fontSize: '15px',
    margin: 0,
    wordBreak: 'break-all',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    fontSize: '13px',
  },
  detailLabel: {
    color: '#6b7280',
  },
  detailValue: {
    color: 'white',
    fontWeight: '500',
  },
});

// ============================================================================
// SNAPSHOT ICON COMPONENT
// ============================================================================
const SnapshotIcon = ({ size = 'small' }) => {
  let width, height;
  
  if (size === 'large') {
    width = '64px';
    height = '64px';
  } else if (size === 'medium') {
    width = '40px';
    height = '40px';
  } else if (size === 'small') {
    width = '24px';
    height = '24px';
  } else {
    width = '40px';
    height = '40px';
  }
  
  return (
    <div style={{ width, height, position: 'relative', flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        <rect
          x="2" y="4" width="20" height="16" rx="2"
          fill="#1b2b1a"
          stroke="#e0eacc"
          strokeWidth="1"
        />
        <path
          d="M6 8 L18 8 L18 16 L6 16 Z"
          fill="#e0eacc"
          fillOpacity="0.2"
        />
        <circle
          cx="12" cy="12" r="3"
          fill="#e0eacc"
        />
        <circle
          cx="15" cy="9" r="1"
          fill="#e0eacc"
        />
      </svg>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const RestoreSnapshotModal = ({ isOpen, onClose, onSubmit, snapshot }) => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();
  
  const [newVmName, setNewVmName] = useState('');
  const [error, setError] = useState('');
  const [lastShownError, setLastShownError] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameCharCount, setNameCharCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const modalRef = useRef(null);
  const isSubmittingRef = useRef(false);
  
  const isSubmitting = loading;
  const styles = getStyles(isSubmitting);

  // Convert errors to toast notifications
  useEffect(() => {
    if (error && error !== lastShownError) {
      notification.error(error);
      setLastShownError(error);
      setError(null);
    }
  }, [error, notification, lastShownError]);
  
  useEffect(() => {
    if (isOpen && snapshot) {
      Sentry.setContext("restore-snapshot-modal", {
        isOpen: true,
        snapshotId: snapshot.snapshot_id || 'unknown',
        snapshotName: snapshot.name || 'unknown',
        snapshotStatus: snapshot.status || 'unknown',
        snapshotSize: snapshot.size || 0,
        vmName: snapshot.vm_name || 'unknown',
        region: snapshot.region || snapshot.vm_environment?.region || 'unknown',
        newVmName: newVmName || 'empty',
        hasError: !!error,
        loading,
        isMobile,
        isTablet
      });
      
      logInfo('Restore snapshot modal opened', {
        snapshotId: snapshot.snapshot_id || 'unknown',
        snapshotName: snapshot.name || 'unknown',
        snapshotStatus: snapshot.status || 'unknown',
        snapshotSize: snapshot.size || 0,
        originalVmName: snapshot.vm_name || 'unknown',
        region: snapshot.region || snapshot.vm_environment?.region || 'unknown',
        createdAt: snapshot.created_at || null,
        isMobile,
        isTablet
      });
    }
  }, [isOpen, snapshot, newVmName, error, loading, isMobile, isTablet]);
  
  useEffect(() => {
    if (isOpen && snapshot) {
      const suggestedName = `${snapshot.name}-restored`;
      setNewVmName(suggestedName);
      setNameCharCount(suggestedName.length);
      setNameError('');
      setError('');
      setLastShownError('');
      setLoading(false);
      isSubmittingRef.current = false;
      
      logInfo('Restore snapshot form initialized', {
        snapshotId: snapshot.snapshot_id || 'unknown',
        snapshotName: snapshot.name || 'unknown',
        suggestedVmName: suggestedName,
        suggestedNameLength: suggestedName.length,
        isMobile,
        isTablet
      });
      
      logUserAction('restore_snapshot_form_initialized', {
        snapshotId: snapshot.snapshot_id || 'unknown',
        snapshotName: snapshot.name || 'unknown',
        suggestedVmName: suggestedName,
        isMobile
      });
    }
  }, [isOpen, snapshot, isMobile, isTablet]);
  
  const handleNameChange = useCallback((e) => {
    const value = e.target.value;
    setNewVmName(value);
    setNameCharCount(value.length);
    
    const validation = validateSnapshotName(value);
    if (!validation.valid) {
      setNameError(validation.error);
    } else {
      setNameError('');
    }
    
    logUserAction('restore_vm_name_change', {
      snapshotId: snapshot?.id || 'unknown',
      newVmName: value,
      charCount: value.length,
      isValid: validation.valid,
      validationError: validation.error || null,
      isMobile
    });
    
    logInfo('Restore VM name changed', {
      snapshotId: snapshot?.id || 'unknown',
      newVmName: value,
      charCount: value.length,
      isValid: validation.valid,
      isMobile
    });
  }, [snapshot?.id, isMobile]);
  
const handleSubmit = useCallback(async () => {
  if (isSubmittingRef.current) {
    return;
  }
  
  isSubmittingRef.current = true;
  
  const startTime = performance.now();
  
  logUserAction('restore_snapshot_submit_initiated', {
    snapshotId: snapshot?.snapshot_id || 'unknown',
    snapshotName: snapshot?.name || 'unknown',
    newVmName,
    charCount: newVmName.length,
    isMobile
  });
  
  logInfo('Restore snapshot form submission started', {
    snapshotId: snapshot?.snapshot_id || 'unknown',
    snapshotName: snapshot?.name || 'unknown',
    newVmName,
    originalVmName: snapshot?.vm_name || 'unknown',
    snapshotSize: snapshot?.size || 0,
    isMobile
  });
  
  const validation = validateSnapshotName(newVmName.trim());
  if (!validation.valid) {
    setNameError(validation.error);
    setError(validation.error);
    
    logError('Restore snapshot validation failed', {
      snapshotId: snapshot?.snapshot_id || 'unknown',
      newVmName,
      validationError: validation.error,
      isMobile
    });
    
    logUserAction('restore_snapshot_validation_failed', {
      snapshotId: snapshot?.snapshot_id || 'unknown',
      newVmName,
      reason: 'invalid_name',
      validationError: validation.error,
      isMobile
    });
    
    isSubmittingRef.current = false;
    return;
  }

  if (!snapshot) {
    setError('Snapshot not found. Please refresh and try again.');
    
    logError('Restore snapshot validation failed - no snapshot', {
      newVmName,
      isMobile
    });
    
    logUserAction('restore_snapshot_validation_failed', {
      newVmName,
      reason: 'snapshot_not_found',
      isMobile
    });
    
    isSubmittingRef.current = false;
    return;
  }

  if (snapshot.status !== 'SUCCESS' && snapshot.status !== 'available') {
    setError(`Snapshot must be available to restore (current: ${snapshot.status})`);
    
    logError('Restore snapshot validation failed - invalid status', {
      snapshotId: snapshot.snapshot_id,
      snapshotStatus: snapshot.status,
      newVmName,
      isMobile
    });
    
    logUserAction('restore_snapshot_validation_failed', {
      snapshotId: snapshot.snapshot_id,
      newVmName,
      reason: 'invalid_snapshot_status',
      snapshotStatus: snapshot.status,
      isMobile
    });
    
    isSubmittingRef.current = false;
    return;
  }
  
  const restoreData = {
    new_vm_name: sanitizeText(newVmName.trim())
  };
  
  setLoading(true);
  
  logInfo('Restore data prepared for submission', {
    snapshotId: snapshot?.snapshot_id || 'unknown',
    restoreData,
    originalSnapshot: {
      name: snapshot?.name || 'unknown',
      size: snapshot?.size || 0,
      status: snapshot?.status || 'unknown'
    },
    isMobile
  });
  
  try {
    await onSubmit(restoreData);
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    logPerformance('restore_snapshot_submit', duration, {
      snapshotId: snapshot?.snapshot_id || 'unknown',
      newVmName: restoreData.new_vm_name,
      snapshotSize: snapshot?.size || 0,
      success: true,
      isMobile
    });
    
    logInfo('Restore snapshot submission completed', {
      snapshotId: snapshot?.snapshot_id || 'unknown',
      newVmName: restoreData.new_vm_name,
      duration: `${duration.toFixed(2)}ms`,
      snapshotSize: snapshot?.size || 0,
      isMobile
    });
    
    logUserAction('restore_snapshot_submitted', {
      snapshotId: snapshot?.snapshot_id || 'unknown',
      newVmName: restoreData.new_vm_name,
      snapshotName: snapshot?.name || 'unknown',
      isMobile
    });
    
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.response?.status === 401 || err.status === 401) {
      handleAuthError();
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 2: Network error (no response)
    if (!err.response) {
      const errorMessage = err.error || getNetworkErrorMessage(err).message;
      const errorCode = err.code || getNetworkErrorMessage(err).code;
      setError(errorMessage);
      logError('Network error in restoreSnapshot', { 
        code: errorCode, 
        originalCode: err.code,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        snapshotId: snapshot?.snapshot_id || 'unknown',
        newVmName: restoreData.new_vm_name
      }, err);
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 3: Rate limit
    if (err.response?.status === 429) {
      const retryAfter = err.response?.headers?.['retry-after'] || 60;
      setError(`Too many requests. Please wait ${retryAfter} seconds.`);
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 4: Backend error code
    if (err.response?.data?.code && ERROR_MESSAGES[err.response.data.code]) {
      setError(ERROR_MESSAGES[err.response.data.code]);
      logError('Restore snapshot error with backend code', {
        code: err.response.data.code,
        duration: `${duration.toFixed(2)}ms`,
        snapshotId: snapshot?.snapshot_id || 'unknown'
      }, err);
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 5: Direct error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Restore snapshot error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`,
        snapshotId: snapshot?.snapshot_id || 'unknown'
      }, err);
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 6: Fallback
    const safeMessage = getSafeErrorMessage(err);
    setError(safeMessage);
    
    logError('Restore snapshot submission failed', {
      snapshotId: snapshot?.snapshot_id || 'unknown',
      newVmName: restoreData.new_vm_name,
      errorName: err.name,
      errorMessage: err.message,
      duration: `${duration.toFixed(2)}ms`,
      isMobile
    }, err);
    
    logUserAction('restore_snapshot_failed', {
      snapshotId: snapshot?.snapshot_id || 'unknown',
      newVmName: restoreData.new_vm_name,
      errorMessage: err.message,
      isMobile
    });
  } finally {
    setLoading(false);
    isSubmittingRef.current = false;
  }
}, [newVmName, onSubmit, snapshot, isMobile, handleAuthError]);  const formatDate = useMemo(() => (dateString) => {
    try {
      const date = new Date(dateString);
      const formatted = date.toLocaleString();
      
      logInfo('Date formatted for snapshot', {
        snapshotId: snapshot?.id || 'unknown',
        originalDate: dateString,
        formattedDate: formatted
      });
      
      return formatted;
    } catch (e) {
      logError('Date formatting failed for snapshot', {
        snapshotId: snapshot?.id || 'unknown',
        dateString,
        errorName: e.name,
        errorMessage: e.message
      }, e);
      
      return 'Unknown date';
    }
  }, [snapshot?.id]);

  const getRegion = useMemo(() => () => {
    if (!snapshot) {
      logInfo('No snapshot available for region extraction');
      return 'Unknown';
    }
    
    let region = 'Unknown';
    if (snapshot.region) {
      region = sanitizeText(snapshot.region);
    } else if (snapshot.vm_environment && snapshot.vm_environment.region) {
      region = sanitizeText(snapshot.vm_environment.region);
    }
    
    logInfo('Region extracted from snapshot', {
      snapshotId: snapshot.snapshot_id || 'unknown',
      region,
      hasDirectRegion: !!snapshot.region,
      hasVmEnvironmentRegion: !!(snapshot.vm_environment && snapshot.vm_environment.region)
    });
    
    return region;
  }, [snapshot]);

  const handleClose = useCallback(() => {
    logUserAction('restore_snapshot_modal_close', {
      snapshotId: snapshot?.id || 'unknown',
      newVmName: newVmName || 'empty',
      hasError: !!error,
      wasLoading: loading,
      isMobile
    });
    
    logInfo('Restore snapshot modal closed', {
      snapshotId: snapshot?.id || 'unknown',
      newVmName: newVmName || 'empty',
      hasError: !!error,
      wasLoading: loading,
      isMobile
    });
    
    setLoading(false);
    isSubmittingRef.current = false;
    onClose();
  }, [onClose, snapshot?.id, newVmName, error, loading, isMobile]);
  
  if (!isOpen || !snapshot) return null;
 
  return (
    <>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #555555 #2a2a2a;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #2a2a2a;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #555555;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #666666;
          }
        `}
      </style>
      
      {isMobile ? (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div 
            ref={modalRef}
            className="bg-[#121212] rounded-lg border border-[#333333] shadow-xl overflow-hidden w-full max-w-sm max-h-[90vh] flex flex-col"
          >
            <div className="flex justify-between items-center border-b border-[#222222] flex-shrink-0 p-4">
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-[#1a1a1a] mr-2">
                  <SnapshotIcon size="small" />
                </div>
                <h2 className="text-lg font-semibold text-white">
                  Restore Snapshot
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-[#1a1a1a] text-gray-400 hover:text-white transition-colors"
              >
                <FaTimes size={14} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <div className="p-4 pb-8">
                <div className="bg-[#1a1a1a] rounded-md border border-[#333333] p-3 mb-4">
                  <h3 className="text-sm font-medium text-[#e0eacc] mb-3 flex items-center">
                    <FaInfoCircle className="mr-2" size={12} />
                    Snapshot Information
                  </h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Name:</span>
                      <span className="text-white font-medium text-right flex-1 ml-2 truncate">{sanitizeText(snapshot.name)}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-400">Size:</span>
                      <span className="text-white">{snapshot.size || 0} GB</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-400">VM:</span>
                      <span className="text-white flex items-center text-right flex-1 ml-2">
                        <FaServer className="text-gray-400 mr-1" size={10} />
                        <span className="truncate">{sanitizeText(snapshot.vm_name) || (snapshot.vm_id ? `VM-${snapshot.vm_id}` : 'Unknown')}</span>
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-400">Region:</span>
                      <span className="text-white">{getRegion()}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span className="text-white">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          snapshot.status === 'SUCCESS' || snapshot.status === 'available'
                            ? 'bg-[#1c2b22] text-[#e0eacc]'
                            : 'bg-yellow-900 bg-opacity-20 text-yellow-400'
                        }`}>
                          {sanitizeText(snapshot.status) || 'Unknown'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-[#1a2b36] border border-[#2a3c4a] p-3 rounded-md mb-4 text-sm">
                  <div className="flex items-start">
                    <FaInfoCircle className="text-[#4a9cff] mt-0.5 mr-2 flex-shrink-0" size={12} />
                    <p className="text-[#8cc8ff] text-sm leading-relaxed">
                      Restoring this snapshot will create a new virtual machine with the same configuration as the original VM when the snapshot was taken.
                    </p>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between mb-2">
                    <label htmlFor="new-vm-name" className="text-gray-300 font-medium text-sm">
                      New VM Name <span className="text-[#ff8080]">*</span>
                    </label>
                    <span className={`text-xs ${nameCharCount > 25 ? 'text-[#ff8080]' : 'text-gray-500'}`}>
                      {nameCharCount}/30
                    </span>
                  </div>
                  <input
                    type="text"
                    id="new-vm-name"
                    value={newVmName}
                    onChange={handleNameChange}
                    className={`w-full border ${nameError ? 'border-[#ff8080]' : 'border-[#333333]'} rounded text-white`}
                    style={{
                      outline: 'none',
                      backgroundColor: '#1d1d1d',
                      height: '44px',
                      fontSize: '14px',
                      padding: '0 12px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                    onBlur={(e) => e.target.style.borderColor = nameError ? '#ff8080' : '#333333'}
                    placeholder="Enter new VM name"
                    maxLength={30}
                    disabled={loading}
                    required
                  />
                  {nameError ? (
                    <p className="mt-1 text-[#ff8080] text-xs">{nameError}</p>
                  ) : (
                    <p className="mt-1 text-gray-500 text-xs">
                      Choose a unique name for your new VM
                    </p>
                  )}
                </div>
                
                <div className="flex flex-col space-y-3 mt-6">
                  <button
                    onClick={handleSubmit}
                    disabled={nameError || !newVmName.trim() || loading}
                    className={`w-full flex items-center justify-center py-3 px-4 text-sm rounded font-medium transition-colors ${
                      nameError || !newVmName.trim() || loading
                        ? 'bg-gray-700 cursor-not-allowed text-gray-400'
                        : 'bg-[#e0eacc] hover:bg-[#c8d9a9] text-black'
                    }`}
                    style={{ minHeight: '44px' }}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-t-black border-black border-opacity-20 rounded-full mr-2"></div>
                        Restoring...
                      </>
                    ) : (
                      <>
                        <FaCheck className="mr-2" size={12} />
                        Restore Snapshot
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={handleClose}
                    className="w-full bg-transparent hover:bg-[rgba(202,217,227,0.1)] text-[#cad9e3] py-3 px-4 rounded border border-[#cad9e3] transition-colors text-sm font-medium"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            {/* LEFT PANEL - Configuration */}
            <div style={styles.leftPanel}>
              {/* Header */}
              <div style={styles.header}>
                <div style={styles.iconWrapper}>
                  <FaRedo size={18} style={{ color: isSubmitting ? '#666' : '#1a1a1a' }} />
                </div>
                <div>
                  <h2 style={styles.title}>Restore Snapshot</h2>
                  <p style={styles.subtitle}>Create new VM from snapshot</p>
                </div>
              </div>

              {/* Source Snapshot Info */}
              <div style={styles.sourceCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <FaInfoCircle size={14} style={{ color: '#e0eacc' }} />
                  <span style={{ color: '#9ca3af', fontSize: '12px', fontWeight: '500' }}>
                    Source Snapshot
                  </span>
                </div>
                <div style={{ color: 'white', fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>
                  {sanitizeText(snapshot.name)}
                </div>
                
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Size</span>
                  <span style={styles.detailValue}>{snapshot.size || 0} GB</span>
                </div>
                
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Original VM</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaServer size={12} style={{ color: '#6b7280' }} />
                    <span style={styles.detailValue}>{sanitizeText(snapshot.vm_name) || (snapshot.vm_id ? `VM-${snapshot.vm_id}` : 'Unknown')}</span>
                  </div>
                </div>
                
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Region</span>
                  <span style={styles.detailValue}>{getRegion()}</span>
                </div>
                
                <div style={{ ...styles.detailRow, marginBottom: 0 }}>
                  <span style={styles.detailLabel}>Status</span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    backgroundColor: snapshot.status === 'SUCCESS' || snapshot.status === 'available' ? '#1c2b22' : 'rgba(161, 98, 7, 0.2)',
                    color: snapshot.status === 'SUCCESS' || snapshot.status === 'available' ? '#e0eacc' : '#fbbf24',
                    fontWeight: '500',
                  }}>
                    {sanitizeText(snapshot.status) || 'Unknown'}
                  </span>
                </div>
              </div>

              {/* Info Box */}
              <div style={styles.infoBox}>
                <FaInfoCircle size={14} style={{ color: '#4a9cff', marginTop: '2px', flexShrink: 0 }} />
                <p style={styles.infoText}>
                  A new VM will be created with the same configuration as when the snapshot was taken
                </p>
              </div>

              {/* New VM Name */}
              <div style={{ marginBottom: '0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <label style={styles.label}>
                    New VM Name<span style={styles.required}>*</span>
                  </label>
                  <span style={{ fontSize: '11px', color: nameCharCount > 25 ? '#ff6b6b' : '#6b7280' }}>
                    {nameCharCount}/30
                  </span>
                </div>
                <input
                  type="text"
                  value={newVmName}
                  onChange={handleNameChange}
                  placeholder="Enter new VM name"
                  disabled={isSubmitting}
                  maxLength={30}
                  style={{
                    ...styles.input,
                    borderColor: nameError ? '#ff6b6b' : '#333',
                  }}
                  onFocus={(e) => !isSubmitting && (e.target.style.borderColor = '#e0eacc')}
                  onBlur={(e) => e.target.style.borderColor = nameError ? '#ff6b6b' : '#333'}
                />
                {nameError && <div style={styles.errorText}>{nameError}</div>}
              </div>
            </div>

            {/* RIGHT PANEL - Summary */}
            <div style={styles.rightPanel}>
              <h3 style={{
                color: '#6b7280',
                fontSize: '11px',
                letterSpacing: '1.5px',
                marginTop: 0,
                marginBottom: '20px',
              }}>SUMMARY</h3>
              
              <div style={{ marginBottom: '16px' }}>
                <p style={styles.summaryLabel}>Snapshot</p>
                <p style={styles.summaryValue}>{sanitizeText(snapshot.name)}</p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <p style={styles.summaryLabel}>Configuration</p>
                <p style={styles.summaryValue}>{snapshot.size || 0} GB</p>
                <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>{getRegion()}</p>
              </div>

              {newVmName && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={styles.summaryLabel}>New VM Name</p>
                  <p style={styles.summaryValue}>{sanitizeText(newVmName)}</p>
                </div>
              )}

              <div style={{ borderTop: '1px solid #222', paddingTop: '20px', marginTop: 'auto' }}></div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !!nameError || !newVmName.trim()}
                style={{
                  ...styles.submitBtn,
                  opacity: (isSubmitting || !!nameError || !newVmName.trim()) && !isSubmitting ? 0.5 : 1,
                }}
              >
                {isSubmitting ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={styles.spinner}></div>
                    <span>Restoring...</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaCheck size={14} />
                    <span>Restore Snapshot</span>
                  </div>
                )}
              </button>
              
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RestoreSnapshotModal;